// src/lib/practice/sage-drill.ts
//
// A drill Sage builds (the generate_drill tool) is a fixed list of question
// ids opened in /practice/session. When it ended, the only way on was
// "Practice Again" — the SAME questions, which a student who just missed some
// has now seen with the answers — and "Back to Topics", a page they never
// came from. What they wanted: more questions like these, and a way back to
// the conversation.
//
// So a Sage drill ends with "Practice more" (the same number of fresh
// questions on the drill's topic, at its difficulty, never the ones just done)
// and "Back to Sage". The fresh set is itself a Sage drill, so it can be
// extended again.
//
// Pure — shared by generate_drill (server) and the session page (client).

export type SageDrillDifficulty = 'E' | 'M' | 'H' | 'mixed'

/** The /practice/session URL for a Sage drill. */
export function sageDrillHref(input: {
  ids: readonly string[]
  label: string
  topic: string
  difficulty: SageDrillDifficulty
}): string {
  const params = new URLSearchParams({
    ids: input.ids.join(','),
    label: input.label,
    from: 'sage',
    drillTopic: input.topic,
    drillDifficulty: input.difficulty,
  })
  return `/practice/session?${params.toString()}`
}

const DIFFICULTY_WORD: Record<SageDrillDifficulty, string> = {
  E: 'easy',
  M: 'medium',
  H: 'hard',
  mixed: '',
}

/**
 * "5 Boundaries questions", "3 hard Linear Functions questions" — a Sage
 * drill's header, its Start card's label and what Sage is told it built. One
 * function so the three can't disagree, and `count` is always the number of
 * questions actually picked: generate_drill once labelled a drill with the
 * number it was ASKED for, so a short topic said "10" and served 4.
 */
export function drillLabel(count: number, topic: string, difficulty: SageDrillDifficulty): string {
  const word = DIFFICULTY_WORD[difficulty] ?? ''
  return `${count} ${word ? `${word} ` : ''}${topic} ${count === 1 ? 'question' : 'questions'}`
}

/**
 * Whether an ids session is a Sage drill. Newer links say `from=sage`; drills
 * Sage built before that carry no `from` at all. Bookmark review is the other
 * ids session, and it says `from=bookmarks`.
 */
export function isSageDrill(from: string | null | undefined, hasIds: boolean): boolean {
  if (!hasIds) return false
  return from === 'sage' || !from
}

/**
 * The drill's topic: the URL's when it has one, else the topic most of its
 * questions share (older links carried none). Null when unknowable.
 */
export function sageDrillTopic(
  urlTopic: string | null | undefined,
  questionTopics: readonly (string | null | undefined)[],
): string | null {
  if (urlTopic) return urlTopic
  const counts = new Map<string, number>()
  for (const t of questionTopics) if (t) counts.set(t, (counts.get(t) ?? 0) + 1)
  let best: string | null = null
  let bestN = 0
  for (const [t, n] of counts) {
    if (n > bestN) {
      best = t
      bestN = n
    }
  }
  return best
}

/** The difficulty to ask for: E/M/H as set, anything else a mixed pool. */
export function sageDrillDifficulty(raw: string | null | undefined): 'E' | 'M' | 'H' | null {
  return raw === 'E' || raw === 'M' || raw === 'H' ? raw : null
}

/** "5 more Linear Functions questions" — the next set's header. */
export function moreDrillLabel(count: number, topic: string): string {
  return `${count} more ${topic} questions`
}

type DrillQuestion = { id: string; topic?: string | null }

export type MoreDrillResult<Q> =
  | { ok: true; questions: Q[]; href: string }
  | { ok: false; notice: string }

/**
 * Find the next set for "Practice more": as many fresh questions as the drill
 * had, on its topic, at its difficulty — falling back to the rest of the
 * topic when that level is used up — never one just done. Topic practice
 * (/api/practice/start) already prefers questions the student hasn't seen.
 * `fetcher` is `fetch`, injected for tests.
 */
export async function loadMoreDrill<Q extends DrillQuestion>(input: {
  done: readonly Q[]
  urlTopic: string | null
  urlDifficulty: string | null
  fetcher: typeof fetch
}): Promise<MoreDrillResult<Q>> {
  const { done, fetcher } = input
  const topic = sageDrillTopic(
    input.urlTopic,
    done.map((q) => q.topic),
  )
  if (!topic) {
    return { ok: false, notice: "We couldn't tell this drill's topic. Ask Sage for another drill." }
  }
  const wanted = sageDrillDifficulty(input.urlDifficulty)
  const doneIds = new Set(done.map((q) => q.id))
  const ask = async (difficulty: 'E' | 'M' | 'H' | null) => {
    const res = await fetcher('/api/practice/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      cache: 'no-store',
      body: JSON.stringify({
        topic,
        count: Math.max(1, done.length),
        exclude: [...doneIds],
        difficulty: difficulty ?? undefined,
      }),
    })
    if (!res.ok) return null
    return (await res.json()) as { questions?: Q[]; emptyDifficulty?: boolean }
  }
  let data = await ask(wanted)
  if (wanted && (!data || data.emptyDifficulty || !data.questions?.length)) data = await ask(null)
  const next = (data?.questions ?? [])
    .filter((q) => !doneIds.has(q.id))
    .map((q) => ({ ...q, topic: q.topic ?? topic }))
  if (next.length === 0) {
    return {
      ok: false,
      notice: `You've been through every ${topic} question we have. Ask Sage for a different topic.`,
    }
  }
  return {
    ok: true,
    questions: next,
    href: sageDrillHref({
      ids: next.map((q) => q.id),
      label: moreDrillLabel(next.length, topic),
      topic,
      difficulty: wanted ?? 'mixed',
    }),
  }
}
